import React, { useMemo } from 'react';
import {
  ChartComponent,
  SeriesCollectionDirective,
  SeriesDirective,
  Inject,
  StepLineSeries,
  DateTime,
  Legend,
  Tooltip,
  Zoom
} from '@syncfusion/ej2-react-charts';
import { DropDownListComponent } from '@syncfusion/ej2-react-dropdowns';
import { useTranslation } from 'react-i18next';
import type { OperationAssignmentDto } from '../api/models/OperationAssignmentDto';
import type { AdditionalResourceDto } from '../api/models/AdditionalResourceDto';
import type { AvailabilityDto } from '../api/models/AvailabilityDto';
import type { ScheduleResource } from './ProductionScheduleViewTypes';

export interface ResourceUtilizationStepLineChartProps {
  timeWindow: { start: Date; end: Date };
  operationAssignments: OperationAssignmentDto[];
  additionalResources: AdditionalResourceDto[];
  availabilities: AvailabilityDto[];
  resources: ScheduleResource[];
  selectedResourceId: string | null;
  onResourceChange: (resourceId: string | null) => void;
  height?: string | number;
}

interface ChartDataPoint {
  date: Date;
  demand: number;
  capacity: number;
}

/** Computes step-line data for demand and capacity for a given resource.
 * Demand includes:
 * - Primary resource: operationAssignments (utilization or 1 per assignment)
 * - Secondary/conditional: additionalResources (quantity per requirement)
 */
function computeChartData(
  resourceId: string,
  timeWindow: { start: Date; end: Date },
  operationAssignments: OperationAssignmentDto[],
  additionalResources: AdditionalResourceDto[],
  availabilities: AvailabilityDto[]
): ChartDataPoint[] {
  const { start, end } = timeWindow;
  const timestamps = new Set<number>();

  timestamps.add(start.getTime());
  timestamps.add(end.getTime());

  const resourceIdStr = String(resourceId);

  operationAssignments
    .filter(a => a.resourceUuid != null && String(a.resourceUuid) === resourceIdStr)
    .forEach(a => {
      if (a.plannedStart) timestamps.add(new Date(a.plannedStart).getTime());
      if (a.plannedEnd) timestamps.add(new Date(a.plannedEnd).getTime());
    });

  additionalResources
    .filter(ar => ar.resourceUuid != null && String(ar.resourceUuid) === resourceIdStr)
    .forEach(ar => {
      if (ar.timeWindowStart) timestamps.add(new Date(ar.timeWindowStart).getTime());
      if (ar.timeWindowEnd) timestamps.add(new Date(ar.timeWindowEnd).getTime());
    });

  availabilities
    .filter(a => a.resourceId != null && String(a.resourceId) === resourceIdStr && a.type === 'ON_SHIFT')
    .forEach(a => {
      if (a.startDateTime) timestamps.add(new Date(a.startDateTime).getTime());
      if (a.endDateTime) timestamps.add(new Date(a.endDateTime).getTime());
    });

  const sortedTimes = Array.from(timestamps).sort((a, b) => a - b);

  const points: ChartDataPoint[] = sortedTimes.map(t => {
    const date = new Date(t);

    let demand = 0;
    // Primary resource: from operationAssignments (OperationModes)
    operationAssignments
      .filter(a => a.resourceUuid != null && String(a.resourceUuid) === resourceIdStr)
      .forEach(a => {
        if (!a.plannedStart || !a.plannedEnd) return;
        const aStart = new Date(a.plannedStart).getTime();
        const aEnd = new Date(a.plannedEnd).getTime();
        if (t >= aStart && t < aEnd) {
          const util = a.utilization != null ? Number(a.utilization) / 100 : 1;
          demand += util;
        }
      });
    // Secondary/conditional resources: from additionalResources (Resource Requirements)
    additionalResources
      .filter(ar => ar.resourceUuid != null && String(ar.resourceUuid) === resourceIdStr)
      .forEach(ar => {
        if (!ar.timeWindowStart || !ar.timeWindowEnd) return;
        const aStart = new Date(ar.timeWindowStart).getTime();
        const aEnd = new Date(ar.timeWindowEnd).getTime();
        if (t >= aStart && t < aEnd) {
          demand += ar.quantity ?? 1;
        }
      });

    let capacity = 0;
    availabilities
      .filter(a => a.resourceId != null && String(a.resourceId) === resourceIdStr && a.type === 'ON_SHIFT')
      .forEach(a => {
        if (!a.startDateTime || !a.endDateTime) return;
        const aStart = new Date(a.startDateTime).getTime();
        const aEnd = new Date(a.endDateTime).getTime();
        if (t >= aStart && t < aEnd) {
          capacity += a.capacityValue ?? 1;
        }
      });

    return { date, demand, capacity };
  });

  return points;
}

const ResourceUtilizationStepLineChart: React.FC<ResourceUtilizationStepLineChartProps> = ({
  timeWindow,
  operationAssignments,
  additionalResources,
  availabilities,
  resources,
  selectedResourceId,
  onResourceChange,
  height = 320
}) => {
  const { t } = useTranslation(['common']);

  const timeWindowStartMs = timeWindow.start.getTime();
  const timeWindowEndMs = timeWindow.end.getTime();
  const chartData = useMemo((): ChartDataPoint[] => {
    if (!selectedResourceId) return [];
    return computeChartData(
      selectedResourceId,
      timeWindow,
      operationAssignments,
      additionalResources,
      availabilities
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- timeWindow is derived from start/end timestamps which are tracked as primitives
  }, [selectedResourceId, timeWindowStartMs, timeWindowEndMs, operationAssignments, additionalResources, availabilities]);

  const resourceOptions = useMemo(
    () =>
      resources
        .filter(r => String(r.id) !== 'unassigned-resource')
        .map(r => ({
          value: String(r.id),
          text: r.name || String(r.id)
        })),
    [resources]
  );

  const maxY = useMemo(() => {
    if (chartData.length === 0) return 1;
    const maxDemand = Math.max(...chartData.map(p => p.demand), 0);
    const maxCapacity = Math.max(...chartData.map(p => p.capacity), 0);
    return Math.max(maxDemand, maxCapacity, 1) * 1.1;
  }, [chartData]);

  if (resources.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500">
        {t('productionSchedule.capacityChart.noResources')}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex items-center gap-2 mb-3">
        <label className="text-sm text-gray-600 whitespace-nowrap">
          {t('productionSchedule.capacityChart.resource')}:
        </label>
        <DropDownListComponent
          dataSource={resourceOptions}
          fields={{ text: 'text', value: 'value' }}
          value={selectedResourceId}
          change={(e: { value?: string | null }) => onResourceChange(e?.value ?? null)}
          placeholder={t('productionSchedule.capacityChart.selectResource')}
          width="200px"
          cssClass="e-small"
        />
      </div>
      <div className="flex-1 min-h-0" style={{ height: typeof height === 'number' ? `${height}px` : height }}>
        {selectedResourceId && chartData.length > 0 ? (
          <ChartComponent
            primaryXAxis={{
              valueType: 'DateTime',
              minimum: timeWindow.start,
              maximum: timeWindow.end,
              labelFormat: 'dd.MM. HH:mm',
              edgeLabelPlacement: 'Shift'
            }}
            primaryYAxis={{
              minimum: 0,
              maximum: maxY,
              title: t('productionSchedule.capacityChart.yAxisTitle')
            }}
            tooltip={{ enable: true }}
            legendSettings={{ visible: true, position: 'Top' }}
            zoomSettings={{
              enableSelectionZooming: true,
              enableMouseWheelZooming: true,
              enablePinchZooming: true,
              mode: 'X',
              showToolbar: true
            }}
            height="100%"
            width="100%"
          >
            <Inject services={[StepLineSeries, DateTime, Legend, Tooltip, Zoom]} />
            <SeriesCollectionDirective>
              <SeriesDirective
                type="StepLine"
                dataSource={chartData}
                xName="date"
                yName="demand"
                name={t('productionSchedule.capacityChart.series.demand')}
                width={2}
                fill="rgba(78, 205, 196, 0.75)"
                marker={{ visible: true, width: 4, height: 4, fill: 'rgba(78, 205, 196, 0.75)' }}
              />
              <SeriesDirective
                type="StepLine"
                dataSource={chartData}
                xName="date"
                yName="capacity"
                name={t('productionSchedule.capacityChart.series.capacity')}
                width={2}
                fill="rgba(95, 39, 205, 0.75)"
                marker={{ visible: true, width: 4, height: 4, fill: 'rgba(95, 39, 205, 0.75)' }}
              />
            </SeriesCollectionDirective>
          </ChartComponent>
        ) : selectedResourceId ? (
          <div className="flex items-center justify-center h-48 text-gray-500">
            {t('productionSchedule.capacityChart.noData', 'Keine Daten für diese Ressource')}
          </div>
        ) : (
          <div className="flex items-center justify-center h-48 text-gray-500">
            {t('productionSchedule.capacityChart.selectResourceHint')}
          </div>
        )}
      </div>
    </div>
  );
};

export default ResourceUtilizationStepLineChart;
